import { api } from './api.ts';

export interface PuttingGame {
    id: string;
    name: string | null;
    holeCount: number;
    status: 'open' | 'finished';
    startedAt: string;
    endedAt: string | null;
    hostId: string | null;
    hostName: string | null;
}

export interface PuttingPlayer {
    userId: string;
    displayName: string;
    avatarUrl: string | null;
    status: 'pending' | 'accepted' | 'declined';
    clubId: string | null;
    clubLabel: string | null;
    clubBrand: string | null;
    joinedAt: string | null;
    leftAt: string | null;
    holesPlayed: number;
    totalPutts: number;
    /** `numeric` de Postgres : arrive en chaîne, jamais en nombre. */
    avgPutts: string | null;
    onePutts: number | null;
    position: number | null;
    /** Scores par numéro de trou : { "1": 2, "2": 1, … }. */
    holes: Record<string, number>;
}

export interface PuttingState {
    game: PuttingGame;
    players: PuttingPlayer[];
    me: PuttingPlayer | null;
}

export interface PuttingInvitation {
    gameId: string;
    name: string | null;
    holeCount: number;
    startedAt: string;
    hostName: string | null;
    hostAvatarUrl: string | null;
    /** L'index de l'hote, ou `null` s'il ne l'a jamais saisi. */
    hostHandicapIndex: number | null;
    invitedAt: string;
    acceptedCount: number;
}

export interface PuttingGameListItem extends PuttingGame {
    myStatus: 'pending' | 'accepted' | 'declined';
    myLeftAt: string | null;
    playerCount: number;
    myHoles: number;
    myPutts: number | null;
}

export interface PuttingClubStat {
    clubId: string;
    label: string;
    brand: string | null;
    model: string | null;
    games: number;
    holes: number;
    totalPutts: number;
    avgPutts: string | null;
    onePutts: number;
    threePutts: number;
}

export interface PuttingStats {
    clubs: PuttingClubStat[];
    overall: {
        games: number;
        holes: number;
        avgPutts: string | null;
        onePutts: number;
        lastPlayedAt: string | null;
    };
}

export const listInvitations = () =>
    api<{ invitations: PuttingInvitation[] }>('/putting/invitations').then((r) => r.invitations);

export const listGames = () =>
    api<{ games: PuttingGameListItem[] }>('/putting/games').then((r) => r.games);

export const getGame = (id: string) => api<PuttingState>(`/putting/games/${id}`);

export const createGame = (body: {
    name?: string | null; holeCount: number; friendIds: string[]; clubId?: string | null;
    /** Rattachement à une séance d'entraînement en cours, quand le putting en
     *  est une étape. Le serveur vérifie que la séance est bien la vôtre. */
    runId?: string | null; runPosition?: number | null;
}) => api<PuttingState>('/putting/games', { method: 'POST', body });

export const acceptGame = (id: string, clubId?: string | null) =>
    api<PuttingState>(`/putting/games/${id}/accept`, { method: 'POST', body: { clubId: clubId ?? null } });

export const declineGame = (id: string) =>
    api<void>(`/putting/games/${id}/decline`, { method: 'POST' });

export const leaveGame = (id: string) =>
    api<PuttingState>(`/putting/games/${id}/leave`, { method: 'POST' });

export const setMyClub = (id: string, clubId: string | null) =>
    api<PuttingState>(`/putting/games/${id}/club`, { method: 'PATCH', body: { clubId } });

export const inviteMore = (id: string, friendIds: string[]) =>
    api<PuttingState>(`/putting/games/${id}/invite`, { method: 'POST', body: { friendIds } });

/** PUT et non POST : sur un green, le réseau va et vient. Rejouer la requête
 *  doit donner le même score, jamais un doublon. */
export const setHoleScore = (id: string, hole: number, putts: number) =>
    api<PuttingState>(`/putting/games/${id}/holes/${hole}`, { method: 'PUT', body: { putts } });

export const clearHoleScore = (id: string, hole: number) =>
    api<PuttingState>(`/putting/games/${id}/holes/${hole}`, { method: 'DELETE' });

/** Retire MA participation et MES scores d'une partie terminée.
 *
 *  Ceux des autres restent : la partie ne m'appartient pas. Quand plus personne
 *  n'y figure, elle disparaît d'elle-même — ce qui règle le cas d'une partie
 *  jouée seul sans avoir eu besoin d'une seconde route. */
export const forgetGame = (id: string) =>
    api<void>(`/putting/games/${id}`, { method: 'DELETE' });

/** Ouvre le flux temps réel d'une partie.
 *
 *  EventSource, et non WebSocket : le trafic est à sens unique — les scores
 *  partent en PUT sur les routes existantes, seul le retour a besoin d'être
 *  poussé — et EventSource reconnecte tout seul là où un WebSocket demanderait
 *  d'écrire la reprise à la main.
 *
 *  Le cookie de session part avec la requête, comme pour tout appel de même
 *  origine ; il n'y a donc pas d'en-tête à poser, ce qu'EventSource ne
 *  permettrait pas.
 *
 *  Rend la fonction de fermeture. */
export function openGameStream(
    id: string,
    onState: (s: PuttingState) => void,
    onStatus: (open: boolean) => void,
): () => void {
    const es = new EventSource(`/api/putting/games/${id}/stream`);

    es.addEventListener('open', () => onStatus(true));
    es.addEventListener('state', (ev) => {
        try {
            onState(JSON.parse((ev as MessageEvent<string>).data) as PuttingState);
            onStatus(true);
        } catch {
            // Trame tronquée : la suivante corrigera, et la relève de secours
            // rattrape de toute façon.
        }
    });
    es.addEventListener('error', () => {
        // EventSource retente seul les coupures réseau. Il abandonne en
        // revanche sur une réponse non conforme — un 401 après expiration du
        // jeton, typiquement. On signale la perte : l'appelant accélère alors
        // sa relève, dont les appels rafraîchissent la session au passage.
        onStatus(false);
    });

    return () => es.close();
}

export const getPuttingStats = () => api<PuttingStats>('/putting/stats');

/** Moyenne lisible : « 1,67 » plutôt que « 1.67 ».
 *
 *  La valeur arrive en chaîne parce que Postgres rend ses `numeric` ainsi —
 *  un double ne peut pas représenter toutes les décimales exactes, et le
 *  pilote refuse de trancher à notre place. */
export const formatAvg = (avg: string | null): string =>
    avg == null ? '—' : Number(avg).toFixed(2).replace('.', ',');
